/**
 * Input Component
 * Accessible text input with validation and sanitization
 */

import { InputHTMLAttributes, forwardRef, useState } from 'react';
import { useSanitize } from '@/hooks/useSanitize';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Input label */
  label?: string;

  /** Error message */
  error?: string;

  /** Helper text */
  helperText?: string;

  /** Icon before input */
  iconBefore?: React.ReactNode;

  /** Icon after input */
  iconAfter?: React.ReactNode;

  /** Enable input sanitization */
  sanitize?: boolean;

  /** Sanitization callback */
  onSanitizedChange?: (value: string) => void;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      iconBefore,
      iconAfter,
      sanitize = false,
      onSanitizedChange,
      onChange,
      className = '',
      id,
      required,
      disabled,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState('');
    const { sanitizeText } = useSanitize();

    const inputId = id || `input-${label?.toLowerCase().replace(/\s+/g, '-')}`;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;

      if (sanitize) {
        value = sanitizeText(value);
        setInternalValue(value);

        if (onSanitizedChange) {
          onSanitizedChange(value);
        }

        // Create synthetic event with sanitized value
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value },
        };
        onChange?.(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
      } else {
        onChange?.(e);
      }
    };

    // Base styles
    const baseStyles = 'ww-block ww-w-full ww-rounded-md ww-shadow-sm focus:ww-ring-2 focus:ww-ring-primary focus:ww-border-primary ww-transition-colors';

    // State styles
    const stateStyles = error
      ? 'ww-border-red-300 ww-text-red-900 ww-placeholder-red-300 focus:ww-ring-red-500 focus:ww-border-red-500'
      : 'ww-border-gray-300 ww-text-gray-900 ww-placeholder-gray-400';

    // Disabled styles
    const disabledStyles = disabled ? 'ww-bg-gray-100 ww-cursor-not-allowed' : 'ww-bg-white';

    // Icon padding
    const paddingStyles = iconBefore
      ? 'ww-pl-10 ww-pr-3 ww-py-2'
      : iconAfter
      ? 'ww-pl-3 ww-pr-10 ww-py-2'
      : 'ww-px-3 ww-py-2';

    const inputClasses = `${baseStyles} ${stateStyles} ${disabledStyles} ${paddingStyles} ${className}`;

    return (
      <div className="ww-w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="ww-block ww-text-sm ww-font-medium ww-text-gray-700 ww-mb-1"
          >
            {label}
            {required && <span className="ww-text-red-500 ww-ml-1">*</span>}
          </label>
        )}

        <div className="ww-relative">
          {iconBefore && (
            <div className="ww-absolute ww-inset-y-0 ww-left-0 ww-pl-3 ww-flex ww-items-center ww-pointer-events-none">
              <span className="ww-text-gray-400" aria-hidden="true">
                {iconBefore}
              </span>
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            className={inputClasses}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={
              error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            disabled={disabled}
            required={required}
            onChange={handleChange}
            value={sanitize ? internalValue : props.value}
            {...props}
          />

          {iconAfter && (
            <div className="ww-absolute ww-inset-y-0 ww-right-0 ww-pr-3 ww-flex ww-items-center ww-pointer-events-none">
              <span className="ww-text-gray-400" aria-hidden="true">
                {iconAfter}
              </span>
            </div>
          )}
        </div>

        {error && (
          <p
            id={`${inputId}-error`}
            className="ww-mt-1 ww-text-sm ww-text-red-600"
            role="alert"
          >
            {error}
          </p>
        )}

        {!error && helperText && (
          <p id={`${inputId}-helper`} className="ww-mt-1 ww-text-sm ww-text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
